/**
 * Cookie consent management utility
 * Compliant with Loi 25 (Quebec privacy law)
 */

export interface CookiePreferences {
  necessary: boolean; // Always true, cannot be disabled
  analytics: boolean;
  marketing: boolean;
  timestamp: number;
}

const COOKIE_CONSENT_KEY = "cookie_consent";
const COOKIE_CONSENT_VERSION = "1.0";

/**
 * Get current cookie preferences from localStorage
 */
export function getCookiePreferences(): CookiePreferences | null {
  try {
    const stored = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (!stored) return null;

    const parsed = JSON.parse(stored);

    // Validate the structure
    if (
      typeof parsed === "object" &&
      typeof parsed.necessary === "boolean" &&
      typeof parsed.analytics === "boolean" &&
      typeof parsed.marketing === "boolean" &&
      typeof parsed.timestamp === "number" &&
      parsed.version === COOKIE_CONSENT_VERSION
    ) {
      return {
        necessary: parsed.necessary,
        analytics: parsed.analytics,
        marketing: parsed.marketing,
        timestamp: parsed.timestamp,
      };
    }

    return null;
  } catch (error) {
    console.error("Error reading cookie preferences:", error);
    return null;
  }
}

/**
 * Save cookie preferences to localStorage
 */
export function saveCookiePreferences(preferences: CookiePreferences): void {
  try {
    const toStore = {
      ...preferences,
      version: COOKIE_CONSENT_VERSION,
      timestamp: Date.now(),
    };
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(toStore));

    // Apply the preferences
    applyCookiePreferences(preferences);
  } catch (error) {
    console.error("Error saving cookie preferences:", error);
  }
}

/**
 * Check if user has made a cookie choice
 */
export function hasUserMadeCookieChoice(): boolean {
  return getCookiePreferences() !== null;
}

/**
 * Accept all cookies
 */
export function acceptAllCookies(): void {
  const preferences: CookiePreferences = {
    necessary: true,
    analytics: true,
    marketing: true,
    timestamp: Date.now(),
  };
  saveCookiePreferences(preferences);
}

/**
 * Refuse all non-necessary cookies
 */
export function refuseAllCookies(): void {
  const preferences: CookiePreferences = {
    necessary: true,
    analytics: false,
    marketing: false,
    timestamp: Date.now(),
  };
  saveCookiePreferences(preferences);
}

/**
 * Apply cookie preferences - enable/disable tracking scripts
 */
export function applyCookiePreferences(preferences: CookiePreferences): void {
  // Analytics cookies
  if (preferences.analytics) {
    enableAnalytics();
  } else {
    disableAnalytics();
  }

  // Marketing cookies
  if (preferences.marketing) {
    enableMarketing();
  } else {
    disableMarketing();
  }
}

/**
 * Enable analytics tracking (Google Analytics, Swetrix, etc.)
 */
function enableAnalytics(): void {
  if (process.env.NODE_ENV === "development") {
    console.log("[Cookies] Analytics enabled");
  }

  // Enable Google Analytics
  if (window.gtag) {
    window.gtag("consent", "update", {
      analytics_storage: "granted",
    });
  }

  // Swetrix is already initialized - no action needed
}

/**
 * Disable analytics tracking
 */
function disableAnalytics(): void {
  if (process.env.NODE_ENV === "development") {
    console.log("[Cookies] Analytics disabled");
  }

  // Disable Google Analytics
  if (window.gtag) {
    window.gtag("consent", "update", {
      analytics_storage: "denied",
    });
  }
}

/**
 * Enable marketing cookies (Facebook Pixel, etc.)
 */
function enableMarketing(): void {
  if (process.env.NODE_ENV === "development") {
    console.log("[Cookies] Marketing cookies enabled");
  }

  // Enable Facebook Pixel
  if (window.fbq) {
    window.fbq("consent", "grant");
  }
}

/**
 * Disable marketing cookies
 */
function disableMarketing(): void {
  if (process.env.NODE_ENV === "development") {
    console.log("[Cookies] Marketing cookies disabled");
  }

  // Disable Facebook Pixel
  if (window.fbq) {
    window.fbq("consent", "revoke");
  }
}

/**
 * Initialize cookie consent on page load
 * Should be called early in the app lifecycle
 */
export function initCookieConsent(): void {
  const preferences = getCookiePreferences();

  if (preferences) {
    // User has made a choice, apply it
    applyCookiePreferences(preferences);
  } else {
    // No choice made yet - disable all non-necessary cookies by default (Loi 25 compliance)
    if (process.env.NODE_ENV === "development") {
      console.log("[Cookies] No consent found, disabling non-essential cookies");
    }
    disableAnalytics();
    disableMarketing();
  }
}

// Type declarations for global tracking functions
declare global {
  interface Window {
    gtag?: (...args: any[]) => void;
    fbq?: (...args: any[]) => void;
  }
}
